import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Calendar, ChevronDown, Scale, Stethoscope } from 'lucide-react';
import { Button } from './ui/button';
import { useAppointment } from '../context/AppointmentContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [referralsOpen, setReferralsOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { openAppointmentModal } = useAppointment();

  const handleDial = (e) => {
    e.preventDefault();
    window.location.href = 'tel:+14043552728';
  };

  const handleNavClick = (e, sectionId) => {
    if (!isHomePage) {
      e.preventDefault();
      window.location.href = `/${sectionId}`;
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Alert Bar */}
      <div className="bg-[#1B1B3A] text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <a
                href="tel:+14043552728"
                onClick={handleDial}
                className="font-medium hover:text-[#20B2AA] transition-colors inline-flex items-center gap-2 cursor-pointer"
                title="Call (404) 355-2728"
              >
                <Phone className="h-4 w-4" />
                <span>(404) 355-2728</span>
                <span className="hidden md:inline">|</span>
                <span className="hidden md:inline">Available 24/7</span>
              </a>
            </div>
            <div className="hidden sm:block text-sm">
              <span className="font-medium">New Patients Welcome</span> • Book an Appointment
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20 md:h-24 lg:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_e50feeb1-fb0e-41c1-8efc-bccb0f886d1e/artifacts/lg1k57g2_Untitled%20design%20%2812%29.png" 
                alt="ATLREGEN" 
                className="h-32 md:h-40 lg:h-52 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <a 
                href={isHomePage ? "#services" : "/#services"} 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Services
              </a>
              <a 
                href={isHomePage ? "#conditions" : "/#conditions"} 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Conditions
              </a>
              <a 
                href={isHomePage ? "#testimonials" : "/#testimonials"} 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Testimonials
              </a>
              <a 
                href={isHomePage ? "#locations" : "/#locations"} 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Locations
              </a>
              <Link 
                to="/blog" 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Blog
              </Link>
              <Link 
                to="/meet-the-doctor" 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                Meet the Doctors
              </Link>
              <Link 
                to="/faq" 
                className="text-gray-700 hover:text-[#E63946] font-medium transition-colors"
              >
                FAQ
              </Link>
              
              {/* Referrals Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setReferralsOpen(true)}
                onMouseLeave={() => setReferralsOpen(false)}
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-[#E63946] font-medium transition-colors py-2">
                  Referrals
                  <ChevronDown className={`h-4 w-4 transition-transform ${referralsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {referralsOpen && (
                  <div className="absolute top-full left-0 pt-2 w-56 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
                      <Link 
                        to="/for-attorneys" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => setReferralsOpen(false)}
                      >
                        <div className="bg-[#E63946]/10 p-2 rounded-lg">
                          <Scale className="h-4 w-4 text-[#E63946]" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">For Attorneys</div>
                          <div className="text-xs text-gray-500">PI Referral Portal</div>
                        </div>
                      </Link>
                      <Link 
                        to="/for-chiropractors" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-t border-gray-100"
                        onClick={() => setReferralsOpen(false)}
                      >
                        <div className="bg-[#20B2AA]/10 p-2 rounded-lg">
                          <Stethoscope className="h-4 w-4 text-[#20B2AA]" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">For Chiropractors</div>
                          <div className="text-xs text-gray-500">Collaborative Care</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="tel:+14043552728">
                <Button 
                  variant="outline" 
                  className="border-2 border-[#20B2AA] text-[#20B2AA] hover:bg-[#20B2AA] hover:text-white font-semibold"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call Now
                </Button>
              </a>
              <Button 
                className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-semibold shadow-lg"
                onClick={openAppointmentModal}
                data-testid="header-free-consultation-btn"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book an Appointment
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            <a 
              href={isHomePage ? "#services" : "/#services"} 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Services
            </a>
            <a 
              href={isHomePage ? "#conditions" : "/#conditions"} 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Conditions
            </a>
            <a 
              href={isHomePage ? "#testimonials" : "/#testimonials"} 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Testimonials
            </a>
            <a 
              href={isHomePage ? "#locations" : "/#locations"} 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Locations
            </a>
            <Link 
              to="/blog" 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link 
              to="/meet-the-doctor" 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Meet the Doctors
            </Link>
            <Link 
              to="/faq" 
              className="text-gray-700 hover:text-[#E63946] font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              FAQ
            </Link>
            
            {/* Mobile Referrals Links */}
            <div className="border-t border-gray-100 pt-2 mt-2">
              <div className="text-sm font-semibold text-gray-500 mb-2">REFERRALS</div>
              <Link 
                to="/for-attorneys" 
                className="flex items-center gap-3 py-2 text-gray-700 hover:text-[#E63946]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Scale className="h-4 w-4 text-[#E63946]" />
                For Attorneys
              </Link>
              <Link 
                to="/for-chiropractors" 
                className="flex items-center gap-3 py-2 text-gray-700 hover:text-[#20B2AA]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Stethoscope className="h-4 w-4 text-[#20B2AA]" />
                For Chiropractors
              </Link>
            </div>

            <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
              <a href="tel:+14043552728">
                <Button 
                  variant="outline" 
                  className="border-2 border-[#20B2AA] text-[#20B2AA] hover:bg-[#20B2AA] hover:text-white w-full"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call Now
                </Button>
              </a>
              <Button 
                className="bg-[#E63946] hover:bg-[#D32F3D] text-white w-full"
                onClick={() => {
                  setMobileMenuOpen(false);
                  openAppointmentModal();
                }}
                data-testid="mobile-free-consultation-btn"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book an Appointment
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
